import { ReactNode } from 'react';
import { Slottable } from '@radix-ui/react-slot';
import { cn } from '@anytag/shared-utils';
import { Icon, IconProps } from '../Icon';
import { Tooltip, TooltipProps } from '../Tooltip';
import { Button as ButtonShadcn } from '../shadcn-blocks/button';
import { CircularProgress, CircularProgressProps } from '../Loading';

interface LoadingProps extends CircularProgressProps {
  status?: boolean;
  title?: ReactNode;
  showIcon?: boolean;
}
export interface ButtonProps extends Omit<React.ComponentProps<typeof ButtonShadcn>, 'title'> {
  title?: ReactNode;
  prefixIcon?: IconProps;
  suffixIcon?: IconProps;
  loading?: boolean | LoadingProps;
  tooltip?: Omit<TooltipProps, 'children'>;
}

export const Button = ({
  title,
  tooltip,
  loading,
  disabled,
  children,
  className,
  prefixIcon,
  suffixIcon,
  ...restProps
}: ButtonProps) => {
  const { isLoading, loadingTitle, showLoadingIcon, reversed } =
    typeof loading === 'boolean'
      ? {
          isLoading: !!loading,
          loadingTitle: undefined,
          showLoadingIcon: false,
          reversed: false
        }
      : {
          isLoading: !!loading?.status,
          loadingTitle: loading?.title,
          showLoadingIcon: !!loading?.showIcon,
          reversed: !!loading?.reversed
        };

  const hasPrefixIcon = (isLoading && showLoadingIcon) || !!prefixIcon;
  const hasSuffixIcon = !!suffixIcon;
  const customTitle = title ? (isLoading && loadingTitle) || title : null;

  return (
    <Tooltip place="top" withinPortal disabled={disabled} {...tooltip}>
      <ButtonShadcn
        disabled={isLoading || disabled}
        className={cn(
          {
            'pr-4 pl-2': hasPrefixIcon && !hasSuffixIcon && !!customTitle, // Prefix icon and title
            'pr-2 pl-4': !hasPrefixIcon && hasSuffixIcon && !!customTitle, // Suffix icon and title
            'pr-2 pl-2': !customTitle && (hasPrefixIcon || hasSuffixIcon), // Prefix or suffix icon only
            'disabled:pointer-events-auto disabled:cursor-default': disabled && tooltip?.help && !tooltip?.disabled // Keep showing tooltip when button disabled
          },
          className
        )}
        {...restProps}
      >
        {isLoading && showLoadingIcon ? (
          <CircularProgress size={18} thickness={0.3} className="bg-black-50" reversed={reversed} />
        ) : prefixIcon ? (
          <Icon size="16px" {...prefixIcon} className={cn('prefix-icon min-w-4', prefixIcon.className)} />
        ) : null}

        {customTitle}
        <Slottable>{children}</Slottable>

        {hasSuffixIcon ? (
          <Icon size="16px" {...suffixIcon} className={cn('suffix-icon min-w-4', suffixIcon?.className)} />
        ) : null}
      </ButtonShadcn>
    </Tooltip>
  );
};
